import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { flashTarget } from './flashTarget';

/**
 * Smoothly scrolls to the element referenced by the URL hash and briefly
 * flashes it so the user can instantly spot the section they landed on.
 *
 * Why this exists:
 * - React Router does not perform native hash scrolling, so pages must do it.
 * - Depending only on `hash` fails when the user clicks the SAME anchor again
 *   (the hash value does not change, so the effect never re-runs). Using
 *   `location.key` re-runs the effect on EVERY navigation, including repeats.
 * - The target section may mount one frame later, so we retry across a few
 *   animation frames before giving up.
 */
export function useHashScroll() {
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;

    let raf = 0;
    let attempts = 0;

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        flashTarget(el);
        return;
      }
      attempts += 1;
      if (attempts < 20) {
        raf = window.requestAnimationFrame(scroll);
      }
    };

    raf = window.requestAnimationFrame(scroll);
    return () => window.cancelAnimationFrame(raf);
  }, [hash, key]);
}

export default useHashScroll;